import { formatTime, parseTime } from "@gigpro/utils/datetime";
import { type UseMutationOptions, useMutation } from "@tanstack/react-query";
import { compareAsc } from "date-fns";
import type { Shift } from "../../generated/model/shift";
import type { ShiftRequest } from "../../generated/model/shiftRequest";
import type { ShiftsListParams } from "../../generated/model/shiftsListParams";
import { getShiftsListQueryOptions, shiftsCreate, shiftsUpdate, useShiftsList } from "../../generated/shifts/shifts";
import { composeSelect, type TimeOnlyInputs } from "../types";

// Shifts store bare backend time strings ("HH:mm:ss"). Reads parse them to `Date`s (sorted by start),
// writes take `Date`s back to the backend format — components own display formatting.
export type ShiftWithDates = TimeOnlyInputs<Shift, "start_time" | "end_time">;
type ShiftWriteData = TimeOnlyInputs<ShiftRequest, "start_time" | "end_time">;

const toShiftDates = (shift: Shift): ShiftWithDates => ({
  ...shift,
  start_time: parseTime(shift.start_time, "backend"),
  end_time: parseTime(shift.end_time, "backend"),
});

const toShiftRequest = ({ start_time, end_time, ...rest }: ShiftWriteData): ShiftRequest => ({
  ...rest,
  start_time: formatTime(start_time, "backend"),
  end_time: formatTime(end_time, "backend"),
});

// The transform retypes the rows, so a consumer `select` sees `ShiftWithDates` rather than the generated `Shift`.
type ShiftsListGenOptions<TData> = NonNullable<Parameters<typeof useShiftsList<TData>>[1]>;
type ShiftsListOptions<TData> = Omit<ShiftsListGenOptions<TData>, "query"> & {
  query?: Omit<NonNullable<ShiftsListGenOptions<TData>["query"]>, "select"> & {
    select?: (data: Array<ShiftWithDates>) => TData;
  };
};

const selectShifts = (shifts: Array<Shift>) =>
  shifts.map(toShiftDates).toSorted((a, b) => compareAsc(a.start_time, b.start_time));

export function useShiftsListWithTransform<TData = Array<ShiftWithDates>>(
  params: ShiftsListParams,
  options?: ShiftsListOptions<TData>,
) {
  return useShiftsList<TData>(params, {
    ...options,
    query: {
      ...options?.query,
      select: composeSelect(selectShifts, options?.query?.select),
    },
  });
}

export const getShiftsListWithTransformQueryOptions = <TData = Array<ShiftWithDates>>(
  params: ShiftsListParams,
  options?: ShiftsListOptions<TData>,
) =>
  getShiftsListQueryOptions<TData>(params, {
    ...options,
    query: {
      ...options?.query,
      select: composeSelect(selectShifts, options?.query?.select),
    },
  });

type CreateVariables = { data: ShiftWriteData };
export function useShiftsCreateWithTransform(options?: {
  mutation?: Omit<UseMutationOptions<ShiftWithDates, unknown, CreateVariables>, "mutationFn">;
}) {
  return useMutation({
    ...options?.mutation,
    mutationFn: async ({ data }: CreateVariables) => toShiftDates(await shiftsCreate(toShiftRequest(data))),
  });
}

type UpdateVariables = { id: number; data: ShiftWriteData };
export function useShiftsUpdateWithTransform(options?: {
  mutation?: Omit<UseMutationOptions<ShiftWithDates, unknown, UpdateVariables>, "mutationFn">;
}) {
  return useMutation({
    ...options?.mutation,
    mutationFn: async ({ id, data }: UpdateVariables) => toShiftDates(await shiftsUpdate(id, toShiftRequest(data))),
  });
}
